import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import { message } from 'antd';
import { useSelector } from 'react-redux';
import { selectTranslations } from 'app/pages/App/slice/selectors';
import { FilterSearch } from './components/FilterSearch';
import MobileFilterSearch from './components/MobileFilterSearch';
import { OrderTable } from './components/OrderTable';
import { QuickApprovalModal } from './components/QuickApprovalModal';
import StyledPagination from 'app/components/StyledPagination';
import RenderIf from 'app/components/RenderIf';
import useWindowSize from 'app/hooks/useWindowSize';
import { HolidayOrder, OrderFilterParams, OrderStatus } from './types';
import { approveOrderApi, rejectOrderApi } from './api';
import { useHistory } from 'react-router-dom';
import { getHolidayOrderListingApi } from 'app/services/holidayApi';
import { getLOVpaymentMethod } from 'app/services/orderApi';
import { useGlobalFunction } from '../App/hooks/useGlobalFunction';
import { getDataAgentApi } from 'app/services/checkTicketApi';

interface HolidayOrderApprovalTabProps {
  hasPermission?: boolean; // Quyền mua vé Combo Holiday
}

export const HolidayOrderApprovalTab: React.FC<
  HolidayOrderApprovalTabProps
> = ({ hasPermission = true }) => {
  const history = useHistory();
  const { device } = useWindowSize();
  const translations = useSelector(selectTranslations);
  const isDesktop = device === 'desktop';
  // orders kept only for potential future use; filteredOrders contains current page
  const [filteredOrders, setFilteredOrders] = useState<HolidayOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<HolidayOrder | null>(null);
  const tableRef = useRef<any>();
  const [currentFilters, setCurrentFilters] = useState<any>({}); // Store current filter params
  const [paymentOptions, setPaymentOptions] = useState<
    Array<{ label: string; value: string }>
  >([]);
  const [agentOptions, setAgentOptions] = useState<
    Array<{ label: string; value: string }>
  >([]);
  const { onOpenDialogNoti, onCloseDialogNoti } = useGlobalFunction();
  const fetchOrders = React.useCallback(
    async (filters?: any, page?: number, pageSize?: number) => {
      setLoading(true);
      try {
        // Only include subOrderState if the caller specified one
        const subOrderState = filters?.subOrderState || 'all';

        const params: any = {
          page: page ?? pagination.current,
          limit: pageSize ?? pagination.pageSize,
          ...filters,
        };
        if (subOrderState !== undefined && subOrderState !== '') {
          params.subOrderState = subOrderState;
        }
        const res = await getHolidayOrderListingApi(params);

        // Handle response - map API data to display format
        if (res && res.data && res.data.data && Array.isArray(res.data.data)) {
          // Map API response to HolidayOrder format
          const mappedOrders: HolidayOrder[] = res.data.data.map(
            (item: any) => {
              // Map backend status value to OrderStatus enum
              const statusMap: Record<string, OrderStatus> = {
                pendingConfirmation: OrderStatus.PENDING_CONFIRMATION,
                confirmed: OrderStatus.CONFIRMED,
                unconfirmed: OrderStatus.UNCONFIRMED,
                cancelled: OrderStatus.CANCELLED,
              };
              // Map tickets to combo format expected by orderItems
              const tickets =
                item?.items?.[0]?.tickets?.map((t: any) => ({
                  name: t.name,
                  quantity: t.quantity,
                })) || [];
              return {
                id: item.idHash || '',
                orderCode: item.holidaySubmitNumber,
                // ensure we keep the third-party submit number returned by API
                submitNumber: item.id || '',
                thirdPartySubmitNumber: item.thirdPartySubmitNumber || '',
                createdAt: item.orderDate,
                paymentMethod: item.payment?.code || '',
                orderItems: [
                  {
                    combo: {
                      quantity: item.totalTicket || 0,
                      name: item.items?.[0]?.productName || '',
                      comboName: item.items?.[0]?.productName || '',
                      salePrice: parseFloat(item.totalPrice) || 0,
                    },
                    flight: {
                      departure: item.items?.[0]?.flights?.[0]?.departure || '',
                      arrival:
                        item.items?.[0]?.flights?.[
                          item.items?.[0]?.flights?.length - 1
                        ]?.arrival ||
                        item.items?.[0]?.flights?.[0]?.arrival ||
                        '',
                      departureTime:
                        item.items?.[0]?.flights?.[0]?.departureTime || '',
                      arrivalTime:
                        item.items?.[0]?.flights?.[
                          item.items?.[0]?.flights?.length - 1
                        ]?.arrivalTime ||
                        item.items?.[0]?.flights?.[0]?.arrivalTime ||
                        '',
                      flightNumber:
                        item.items?.[0]?.flights?.[0]?.flightNumber || '',
                    },
                    tickets: tickets,
                  },
                ],
                totalPayment: parseFloat(item.totalPrice) || 0,
                expiredAt: item.expiredAt,
                agent: item.agent?.name || '',
                subOrderState:
                  statusMap[item.subOrderState?.value] || OrderStatus.CANCELLED,
                agentConfirmed: item.agentConfirmed?.name || '',
                logConfirmation: item?.logConfirmation || [],
                confirmedAt: item?.confirmedAt || '',
              };
            },
          );

          setFilteredOrders(mappedOrders);
          setPagination(prev => ({
            ...prev,
            current: page ?? prev.current,
            pageSize: pageSize ?? prev.pageSize,
            total: res.data.paginator?.totalCount ?? 0,
          }));
        } else {
          setFilteredOrders([]);
          setPagination(prev => ({
            ...prev,
            current: page ?? prev.current,
            pageSize: pageSize ?? prev.pageSize,
            total: 0,
          }));
        }
      } catch (error) {
        // message.error(
        //   translations?.['b2b.txt.errorLoadOrders'] ||
        //     'Không thể tải danh sách đơn hàng',
        // );
        setFilteredOrders([]);
        setPagination(prev => ({ ...prev, total: 0 }));
      } finally {
        setLoading(false);
      }
    },
    [pagination, translations],
  );

  // Fetch data and lookup values on mount
  React.useEffect(() => {
    fetchOrders(undefined, 1, pagination.pageSize);
    getPaymentMethodOptions();
    fetchAgentList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // agentOptions now sourced from separate API call; state updated by fetchAgentList

  // Fetch payment method options helper
  const getPaymentMethodOptions = React.useCallback(async () => {
    try {
      const { data } = await getLOVpaymentMethod();
      if (data && Array.isArray(data.data)) {
        const opts = data.data.map((item: any) => ({
          label: item.name,
          value: item.id,
        }));
        setPaymentOptions([
          { label: translations?.['b2b.txt.filterAll'] || 'Tất cả', value: '' },
          ...opts,
        ]);
      }
    } catch (e) {
      // ignore or log
      console.warn('Failed to load payment methods', e);
    }
  }, [translations]);
  // Fetch agent list for filter dropdown
  const fetchAgentList = React.useCallback(async () => {
    try {
      const res = await getDataAgentApi();
      if (res && res.data && Array.isArray(res.data.data)) {
        const names = res?.data?.data?.map(agent => ({
          label: agent.name,
          value: agent.id,
        }));
        setAgentOptions([
          { label: translations?.['b2b.txt.filterAll'] || 'Tất cả', value: '' },
          ...names,
        ]);
      }
    } catch (e) {
      console.warn('error fetching agents for filter', e);
    }
  }, [translations]);

  // Filter logic - call API with filter params
  const handleSearch = (searchFilters: OrderFilterParams) => {
    // Map filter params to API params
    const apiFilters: any = {};

    if (searchFilters.orderDate) {
      apiFilters.orderDate = searchFilters.orderDate;
    }

    if (searchFilters.status) {
      // Map status to backend format if needed
      const stateMap: Record<string, string> = {
        PENDING_CONFIRMATION: 'pendingConfirmation',
        CONFIRMED: 'confirmed',
        UNCONFIRMED: 'unconfirmed',
        CANCELLED: 'cancelled',
        ALL: 'all',
      };
      apiFilters.subOrderState =
        stateMap[searchFilters.status] || searchFilters.status;
    }

    if (searchFilters.orderCode) {
      apiFilters.orderNumber = searchFilters.orderCode;
    }

    if (searchFilters.paymentMethod) {
      apiFilters.paymentMethod = searchFilters.paymentMethod;
    }

    if (searchFilters.agent) {
      apiFilters.agentId = searchFilters.agent;
    }

    // Save current filters for pagination
    setCurrentFilters(apiFilters);

    // Reset to page 1 when search
    setPagination(prev => ({ ...prev, current: 1 }));

    // Fetch orders with filter params, page 1, and current pageSize
    fetchOrders(apiFilters, 1, pagination.pageSize);
  };

  const handleApprove = (order: HolidayOrder) => {
    setSelectedOrder(order);
    setModalVisible(true);
  };

  const handleOrderExpired = (idHash: string) => {
    // Update local state to mark the order as cancelled when countdown expires
    setFilteredOrders(prevOrders => {
      const safePrev = Array.isArray(prevOrders) ? prevOrders : [];
      return safePrev.map(order =>
        order.id === idHash
          ? { ...order, subOrderState: OrderStatus.CANCELLED }
          : order,
      );
    });

    message.warning(
      translations?.['b2b.txt.orderExpiredMsg'] ||
        'Đơn hàng đã hết thời gian giữ',
    );

    // Optionally fetch from backend to sync
    // fetchOrders();
  };

  const handleApproveConfirm = async (idHash: string) => {
    try {
      const response = await approveOrderApi(idHash);

      // Update order status in local state
      setFilteredOrders(prevOrders => {
        const safePrev = Array.isArray(prevOrders) ? prevOrders : [];
        return safePrev.map(order =>
          order.id === idHash
            ? { ...order, subOrderState: OrderStatus.CONFIRMED }
            : order,
        );
      });

      // message.success(
      //   response.message ||
      //     translations?.['b2b.txt.approveSuccess'] ||
      //     'Phê duyệt đơn hàng thành công',
      // );
      setModalVisible(false);

      // Optionally refresh data
      // fetchOrders();
    } catch (error: any) {
      // Handle specific error codes
      let messageError = '';
      if (error.errorCode === 'MSG021') {
        messageError =
          translations?.['MSG021'] ??
          'Đơn hàng đã được phê duyệt/từ chối bởi lãnh đạo khác';
      } else if (error.errorCode === 'MSG022') {
        messageError =
          translations?.['MSG022'] ?? 'Đơn hàng đã bị hủy bởi sale';
      } else if (error.errorCode === 'MSG023') {
        messageError =
          translations?.['MSG023'] ?? 'Đơn hàng đã hết thời gian giữ';
      } else {
        messageError =
          error.message ||
          translations?.['b2b.txt.approveError'] ||
          'Có lỗi xảy ra khi phê duyệt đơn hàng';
      }

      // Close modal and refresh data after error
      setModalVisible(false);
      onOpenDialogNoti({
        type: 'ERROR',
        isShow: true,
        title: translations?.['b2b.txt.payment-deposit-fail'] ?? '',
        content: messageError,
        maskClosable: false,
        onCloseDialog: () => {
          onCloseDialogNoti();
          fetchOrders();
        },
      });
    }
  };

  const handleRejectConfirm = async (idHash: string) => {
    try {
      const response = await rejectOrderApi(idHash);

      // Update order status in local state
      setFilteredOrders(prevOrders => {
        const safePrev = Array.isArray(prevOrders) ? prevOrders : [];
        return safePrev.map(order =>
          order.id === idHash
            ? { ...order, subOrderState: OrderStatus.UNCONFIRMED }
            : order,
        );
      });

      // message.success(
      //   response.message ||
      //     translations?.['b2b.txt.rejectSuccess'] ||
      //     'Từ chối đơn hàng thành công',
      // );
      setModalVisible(false);

      // Optionally refresh data
      // fetchOrders();
    } catch (error: any) {
      // Handle specific error codes
      let messageError = '';
      if (error.errorCode === 'MSG021') {
        messageError =
          translations?.['MSG021'] ??
          'Đơn hàng đã được phê duyệt/từ chối bởi lãnh đạo khác';
      } else if (error.errorCode === 'MSG022') {
        messageError =
          translations?.['MSG022'] ?? 'Đơn hàng đã bị hủy bởi sale';
      } else if (error.errorCode === 'MSG023') {
        messageError =
          translations?.['MSG023'] ?? 'Đơn hàng đã hết thời gian giữ';
      } else {
        messageError =
          error.message ||
          translations?.['b2b.txt.rejectError'] ||
          'Có lỗi xảy ra khi từ chối đơn hàng';
      }

      // Close modal and refresh data after error
      setModalVisible(false);
      onOpenDialogNoti({
        type: 'ERROR',
        isShow: true,
        title: translations?.['b2b.text.notify'] ?? '',
        content: messageError,
        maskClosable: false,
        onCloseDialog: () => {
          onCloseDialogNoti();
          fetchOrders();
        },
      });
    }
  };

  const handleCloseModal = () => {
    setModalVisible(false);
    setSelectedOrder(null);
  };

  const handleViewDetail = (orderCode: string) => {
    // Navigate to order detail page
    // history.push(`/holiday/approval/${orderCode}`);
    history.push(`/holiday/order-detail/${orderCode}`);
  };

  const handlePaginationChange = (page: number, pageSize: number) => {
    setPagination(prev => ({ ...prev, current: page, pageSize }));

    // Fetch data with current filters when pagination changes
    fetchOrders(currentFilters, page, pageSize);

    // Scroll to top when page changes
    window.scrollTo(0, 0);
    tableRef?.current?.querySelector('.ant-table-body')?.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  // If no permission, show message
  if (!hasPermission) {
    return (
      <NoPermissionWrapper>
        <NoPermissionText>
          {translations?.['b2b.txt.noPermission'] ||
            'Bạn không có quyền truy cập chức năng này.'}
        </NoPermissionText>
      </NoPermissionWrapper>
    );
  }

  return (
    <>
      <RenderIf
        condition={isDesktop}
        fallback={
          <MobileFilterSearch
            onSearch={handleSearch}
            agentOptions={agentOptions}
            paymentOptions={paymentOptions}
          />
        }
      >
        <FilterSearch
          onSearch={handleSearch}
          agentOptions={agentOptions}
          paymentOptions={paymentOptions}
        />
      </RenderIf>
      <div style={{ marginTop: 24 }}>
        <OrderTable
          data={filteredOrders}
          loading={loading}
          onApprove={handleApprove}
          onViewDetail={handleViewDetail}
          onExpired={handleOrderExpired}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
          }}
          tableRef={tableRef}
        />
        <StyledPagination
          style={{
            marginTop: '40px',
            display: 'flex',
          }}
          showSizeChanger
          defaultCurrent={1}
          current={pagination.current}
          total={pagination.total}
          pageSize={pagination.pageSize}
          onChange={handlePaginationChange}
          pageSizeOptions={['20', '50', '100']}
        />
      </div>
      <QuickApprovalModal
        visible={modalVisible}
        order={selectedOrder}
        onClose={handleCloseModal}
        onApprove={handleApproveConfirm}
        onReject={handleRejectConfirm}
        translations={translations}
      />
    </>
  );
};

const NoPermissionWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  background: #fff;
  border-radius: 8px;
  margin: 16px;
`;

const NoPermissionText = styled.div`
  font-size: 16px;
  color: #8c8c8c;
  text-align: center;
`;

const NoDataWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  background: #fff;
  border-radius: 8px;
  margin-top: 24px;
`;

const NoDataText = styled.div`
  font-size: 14px;
  color: #8c8c8c;
  text-align: center;
`;
